import Layout from "antd/lib/layout";
import clsx from "clsx";
import { Set } from "immutable";
import { useRouter } from "next/router";
import { ComponentPropsWithoutRef, ReactNode, useRef } from "react";
import { DarkModeSwitch } from "src/components/shared/AppThemeProvider";

import { authenticatedFeatures, getFeaturesWithoutClassroom, resolveNativeAdminRouteAccess } from "@sol/access-control";
import { AuthenticatedUserProvider } from "@sol/authentication/AuthenticatedUserContext";
import { useAuthenticationStore } from "@sol/authentication/useAuthenticationStore";
import { ActiveClassroomProvider, ClassroomsProvider } from "@sol/classrooms";
import { checkFeatureFlag, Feature, FeaturesProvider, useFeatures } from "@sol/features";
import { Route } from "@sol/routing";
import { UserRole } from "@sol/sdk";

import { Announcements } from "@/features/announcements";
import { BrandSelect, isBrandScopeShown } from "@/features/brand-select";
import useLocale from "@/shared/hooks/useLocale";
import { NotificationMenu } from "@/widgets/notification-menu";
import { Sidebar } from "@/widgets/sidebar";

import { ScrollProvider } from "../../../contexts/ScrollContext";
import HttpErrorBoundary from "../../HttpErrorBoundary";
import HttpErrorDisplay from "../../HttpErrorDisplay";
import { SkipLinks } from "../SkipLinks";
import { AuthenticatedLayoutFooter } from "./AuthenticatedLayoutFooter";

type AuthenticatedLayoutProps = {
    children: ReactNode;
};

const { Content, Header } = Layout;

export function AuthenticatedLayoutContent({
    className,
    children,
    style,
    ...props
}: ComponentPropsWithoutRef<typeof Content>) {
    return (
        <Content
            id="main-content"
            className={clsx("flex min-h-[auto]! flex-col p-4 pt-0 [grid-area:content]", className)}
            style={style}
            {...props}
        >
            {children}
        </Content>
    );
}

export function AuthenticatedLayoutHeader({ className, children, ...props }: ComponentPropsWithoutRef<typeof Header>) {
    return (
        <Header className={clsx("overflow-hidden p-4 pr-0 [grid-area:header]", className)} {...props}>
            {children}
        </Header>
    );
}

const TUTOR_WITHOUT_LEARNERS_FEATURES = Set(authenticatedFeatures);
export function AuthenticatedLayout({ children }: AuthenticatedLayoutProps) {
    const [locale, setLocale] = useLocale();
    const router = useRouter();
    const { userId, logout } = useAuthenticationStore();
    const features = useFeatures();
    const scrollRef = useRef<HTMLDivElement>(null);

    return (
        <>
            <AuthenticatedUserProvider userId={userId}>
                {({ user, settings }) => {
                    if (!(user && settings)) {
                        return null;
                    }

                    // Update UI locale if different from user locale
                    const userLocale = user.language.locale;
                    if (!!userLocale && locale !== userLocale) {
                        setLocale(userLocale);
                    }

                    const role = user.roles[0];

                    const isTutor = role === UserRole.TUTOR;

                    return (
                        <ClassroomsProvider queryKey={`user-${userId}-classrooms`} disabled={false}>
                            {({ classrooms }) => {
                                let userFeatures = features;
                                if (isTutor) {
                                    // A tutor's access is scoped to his learners (tutorés), not classrooms.
                                    // A tutor with no learners is reduced to PROFILE; a tutor with learners
                                    // keeps his full feature set regardless of classroom membership.
                                    userFeatures =
                                        (user.learnersAsTutor.length ?? 0) === 0
                                            ? TUTOR_WITHOUT_LEARNERS_FEATURES
                                            : features;
                                } else if (classrooms && classrooms.length === 0) {
                                    userFeatures = getFeaturesWithoutClassroom(role, userFeatures);
                                }

                                if (process.env.ENV === "local") {
                                    userFeatures = userFeatures.add(Feature.DARK_MODE);
                                }

                                const checkUserFeatureFlag = (features: Feature | Feature[]) =>
                                    checkFeatureFlag(userFeatures, features);
                                const showAnnouncementsWidget = router.pathname !== Route.ANNOUNCEMENTS;
                                const nativeAdminRouteAccess = resolveNativeAdminRouteAccess(
                                    router.pathname as Route,
                                    userFeatures,
                                );

                                return (
                                    <FeaturesProvider features={userFeatures}>
                                        <ActiveClassroomProvider classrooms={classrooms}>
                                            <ScrollProvider scroll={scrollRef.current}>
                                                <Layout className="h-screen lg:overflow-hidden" hasSider>
                                                    <Sidebar currentUser={user} logout={logout} />
                                                    <Layout
                                                        ref={scrollRef}
                                                        style={{
                                                            gridTemplateAreas: showAnnouncementsWidget
                                                                ? '"header notifications" "announcements announcements" "content content" "footer footer"'
                                                                : '"header notifications" "content content" "footer footer"',
                                                        }}
                                                        className={clsx(
                                                            "relative grid min-h-screen [scrollbar-gutter:stable] grid-cols-[1fr_auto] overflow-auto",
                                                            showAnnouncementsWidget
                                                                ? "grid-rows-[auto_auto_1fr_auto]"
                                                                : "grid-rows-[auto_1fr_auto]",
                                                        )}
                                                    >
                                                        <div className="p-4 pl-0 [grid-area:notifications]">
                                                            <div className="flex flex-wrap items-center justify-end gap-ant-xs sm:flex-nowrap sm:gap-ant">
                                                                {isBrandScopeShown(router.pathname) ? (
                                                                    <BrandSelect />
                                                                ) : null}
                                                                <div className="ml-auto flex items-center gap-ant-xs sm:gap-ant">
                                                                    {checkUserFeatureFlag(Feature.DARK_MODE) ? (
                                                                        <DarkModeSwitch />
                                                                    ) : null}
                                                                    <NotificationMenu />
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {showAnnouncementsWidget && (
                                                            <Announcements className="[grid-area:announcements]" />
                                                        )}

                                                        <HttpErrorBoundary>
                                                            {nativeAdminRouteAccess === "forbidden" ? (
                                                                <div data-cy="native-admin-access-denied">
                                                                    <HttpErrorDisplay status={403} />
                                                                </div>
                                                            ) : (
                                                                children
                                                            )}
                                                        </HttpErrorBoundary>

                                                        <AuthenticatedLayoutFooter className="[grid-area:footer]" />
                                                    </Layout>
                                                </Layout>
                                            </ScrollProvider>
                                        </ActiveClassroomProvider>
                                    </FeaturesProvider>
                                );
                            }}
                        </ClassroomsProvider>
                    );
                }}
            </AuthenticatedUserProvider>
            <SkipLinks />
        </>
    );
}
